import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { onValue, ref } from 'firebase/database'
import { QRCodeCanvas } from 'qrcode.react'

import { db } from '../lib/firebase.js'
import CLUB from '../config/club.js'

// Short-link resolver for /live (and live.pbccgolf.com). Finds the currently-
// live event and forwards to the existing follow-live hub at /hub?t={id}, so one
// link never goes stale. Prefers a REAL (non-demo), non-closed tournament — most
// recent by createdAt, the same rule the TV boards use. If there's no real event
// yet, it FALLS BACK to the most-recent demo/practice (by demoCreatedAt) so /live
// still lands on the current demo. Read-only; nothing operator/admin, no AI circle.
//
// SAVEABLE-QR GATE: the FIRST time a spectator scans in on a device, we show a
// big saveable QR of the durable /live link ("tap and hold to save to Photos")
// so they can get back in if they lose the page. A localStorage "seen" flag makes
// it show ONCE per device — returning spectators skip straight to the hub. The
// gate lives here (the /live entry) ONLY, so players who reach /hub from the
// player app never see it. Mirrors the dining tool's proven raster-PNG approach.
const GREEN = '#123D26'
const CREAM = '#F7F2E6'
const SEEN_KEY = 'liveQrSeen'

export default function LiveRedirect() {
  const [state, setState] = useState({ loading: true, id: null })
  // Gate shows unless this device has already seen it. Read synchronously so we
  // never flash the gate for a returning spectator.
  const [entered, setEntered] = useState(() => {
    try { return localStorage.getItem(SEEN_KEY) === '1' } catch { return false }
  })
  const [qrPng, setQrPng] = useState('') // real PNG data-URL so long-press = Save to Photos

  // Durable spectator link — always resolves back through THIS resolver to
  // whatever tournament is live, so a saved QR never goes stale.
  const liveUrl = typeof window !== 'undefined' ? `${window.location.origin}/live` : '/live'

  useEffect(() => {
    const off = onValue(
      ref(db, 'tournaments'),
      (snap) => {
        const all = snap.val() || {}
        const open = Object.entries(all).filter(([, v]) => v?.info?.status !== 'closed')

        // 1) Prefer a real (non-demo) open tournament — most recent first.
        const real = open
          .filter(([, v]) => !v?.info?.isDemo)
          .sort((a, b) => (b[1]?.info?.createdAt || 0) - (a[1]?.info?.createdAt || 0))
        if (real[0]) { setState({ loading: false, id: real[0][0] }); return }

        // 2) Fall back to the most-recent demo/practice by demoCreatedAt.
        const demo = open
          .filter(([, v]) => !!v?.info?.isDemo)
          .sort((a, b) =>
            ((b[1]?.info?.demoCreatedAt || b[1]?.info?.createdAt || 0) -
             (a[1]?.info?.demoCreatedAt || a[1]?.info?.createdAt || 0)))
        setState({ loading: false, id: demo[0]?.[0] || null })
      },
      () => setState({ loading: false, id: null }),
    )
    return () => off()
  }, [])

  // Read the off-screen QR canvas into a PNG data-URL. Long-press "Save to Photos"
  // (iOS) / "Download image" (Android) only works on a real <img>/raster — never
  // on a canvas or SVG — so the gate shows this PNG, not the canvas.
  useEffect(() => {
    if (entered) return
    const t = setTimeout(() => {
      const c = document.querySelector('[data-live-qr] canvas')
      if (c && c.toDataURL) { try { setQrPng(c.toDataURL('image/png')) } catch { /* ignore */ } }
    }, 90)
    return () => clearTimeout(t)
  }, [liveUrl, entered])

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, '1') } catch { /* ignore */ }
    setEntered(true)
  }

  // ── SAVEABLE-QR GATE — shown FIRST, once per device ──
  if (!entered) {
    return (
      <div style={{ minHeight: '100vh', background: GREEN, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 18px', fontFamily: '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif' }}>
        <div style={{ background: CREAM, borderRadius: 24, border: `3px solid ${GREEN}`, padding: 24, textAlign: 'center', maxWidth: 380, width: '100%' }}>
          {/* Off-screen canvas → read to PNG. The visible QR is a real <img> so
              long-press gives the native Save to Photos / Download image. */}
          <div data-live-qr style={{ position: 'absolute', left: -99999, top: 0 }} aria-hidden="true">
            <QRCodeCanvas value={liveUrl} size={512} bgColor="#FFFFFF" fgColor={GREEN} includeMargin />
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 18 }}>
            {qrPng
              ? <img src={qrPng} alt="Live tournament QR code — tap and hold to save" width={260} height={260} style={{ width: 260, height: 260, display: 'block' }} />
              : <div style={{ width: 260, height: 260 }} />}
          </div>
          <div style={{ color: GREEN, fontWeight: 800, fontSize: 18, lineHeight: 1.45 }}>
            Tap and hold to save to your Photos
          </div>
          <div style={{ color: GREEN, fontWeight: 600, fontSize: 15, lineHeight: 1.45, marginTop: 8, opacity: 0.85 }}>
            If you lose this page, scan the saved code to jump right back in.
          </div>
        </div>
        <button
          onClick={dismiss}
          style={{ marginTop: 24, background: CREAM, color: GREEN, border: 'none', borderRadius: 999, padding: '15px 44px', fontWeight: 900, fontSize: 18 }}>
          Continue →
        </button>
      </div>
    )
  }

  if (state.loading) {
    return (
      <div
        className="min-h-full flex items-center justify-center"
        style={{ backgroundColor: CLUB.backgroundColor, color: '#FFFFFF' }}>
        <p className="text-lg">Loading…</p>
      </div>
    )
  }

  if (state.id) return <Navigate to={`/hub?t=${state.id}`} replace />

  return (
    <div
      className="min-h-full flex items-center justify-center px-6 text-center"
      style={{ backgroundColor: CLUB.backgroundColor }}>
      <div style={{ color: '#FFFFFF' }}>
        <div className="text-5xl mb-3">⛳</div>
        <p className="text-xl">No live tournament right now — check back soon.</p>
      </div>
    </div>
  )
}
